import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Globe2 } from "lucide-react";
import { MaskLines, Reveal } from "./Motion";
import Counter from "./Counter";
import { IMG } from "../../data/content";

const CREDS = [
  { value: 42, suffix: "+", label: "Years of international experience" },
  { value: 35, suffix: "+", label: "Years of global work experience" },
  { value: 50, prefix: "$", suffix: "M+", label: "Business generated" },
  { value: 10, suffix: "+", label: "Domain specialties" },
];

function ProfileCard() {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rx = useSpring(useTransform(y, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(x, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - r.left) / r.width - 0.5);
    y.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { x.set(0); y.set(0); };
  return (
    <div style={{ perspective: 1100 }} className="mx-auto w-full max-w-[420px]">
      <motion.div
        data-testid="founder-profile-card"
        onMouseMove={onMove}
        onMouseLeave={reset}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative rounded-[2rem] border border-forest/25 bg-white p-5 shadow-[0_40px_80px_-30px_rgba(0,125,108,.45)]"
      >
        <div className="arch relative aspect-[4/5] overflow-hidden bg-[radial-gradient(60%_50%_at_50%_35%,rgba(119,200,211,.65),#007D6C_75%)]">
          <img src={IMG("founder.jpg")} alt="Clive Fernandes in a traditional Himachali pagadi holding Himachal cider" className="h-full w-full object-cover object-top mix-blend-normal" />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
        </div>
        <div style={{ transform: "translateZ(50px)" }} className="absolute -right-4 top-8 rotate-6 rounded-full bg-gold px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-forest-deep shadow-xl">
          World first
        </div>
        <div style={{ transform: "translateZ(40px)" }} className="relative -mt-20 px-3 pb-3">
          <p className="font-sign text-5xl text-forest">Indian Cider Man</p>
          <p className="mt-1 font-display text-3xl font-semibold text-forest-deep">Clive Fernandes</p>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-forest-deep/80">Visionary · Global entrepreneur</p>
        </div>
      </motion.div>
    </div>
  );
}

export default function Founder() {
  return (
    <section id="founder" className="relative overflow-hidden bg-mist py-24 text-forest-deep lg:py-36" data-testid="founder-section">
      <div className="absolute inset-0 bg-[radial-gradient(40%_40%_at_20%_40%,rgba(255,255,255,.65),transparent_70%)]" />
      <div className="wrap relative grid grid-cols-1 items-center gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5"><ProfileCard /></div>
        <div className="lg:col-span-7">
          <p className="eyebrow text-forest">Chapter 03 — Meet the Founder</p>
          <MaskLines className="mt-6 font-display text-5xl font-semibold leading-[0.98] sm:text-6xl lg:text-7xl" lines={["The Indian", <span key="c" className="italic text-forest">Cider Man.</span>]} />
          <Reveal>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-forest-deep md:text-lg">
              Clive Fernandes is the first person in the world to brew craft cider in India and export 100% of it to the UK. Since 2014 he has made cider the honest way: real Himachali apples and no shortcuts.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 border-t border-forest-deep/15">
            {CREDS.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.08} className={`border-b border-forest-deep/15 py-7 ${i % 2 === 0 ? "pr-4 sm:pr-8" : "border-l pl-4 sm:pl-8"}`}>
                <Counter value={c.value} prefix={c.prefix} suffix={c.suffix} className="font-mono text-4xl font-medium tracking-tight text-forest sm:text-5xl" testId={`founder-cred-${i}`} />
                <p className="mt-2 text-sm text-forest-deep/85">{c.label}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 flex items-start gap-4 rounded-2xl border border-forest/30 bg-white/60 p-6">
            <Globe2 className="mt-1 h-6 w-6 shrink-0 text-forest" />
            <div>
              <p className="font-display text-2xl italic leading-snug">“Sharing the ‘JAI <span className="font-deva not-italic">हो!</span>’ joy of our harvest in every bottle.”</p>
              <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-forest-deep/80">Core philosophy</p>
            </div>
          </Reveal>
          <Reveal className="mt-6 grid grid-cols-2 gap-4">
            <img src={IMG("user-3.jpg")} alt="A husky visiting the Himachal Cider stall at a UK festival" className="frame-clip aspect-[4/3] w-full object-cover" />
            <img src={IMG("user-4.jpg")} alt="Early Himachal cider batches against the Himalayan hills" className="aspect-[4/3] w-full rounded-2xl object-cover" />
          </Reveal>
        </div>
      </div>
    </section>
  );
}
